import { useMemo } from "react";
import { Check } from "lucide-react";
import { phaseColorMap } from "@/lib/mockData";
import { cn } from "@/lib/utils";

const STATUS_PILL = {
  done: {
    label: "DONE",
    icon: Check,
    chip: "bg-emerald-50 text-emerald-700",
    dot: "#10B981",
  },
  in_progress: {
    label: "IN PROGRESS",
    icon: null,
    chip: "bg-violet-50 text-violet-700",
    dot: "#7C3AED",
  },
  upcoming: {
    label: "UPCOMING",
    icon: null,
    chip: "bg-zinc-100 text-zinc-600",
    dot: "#9CA3AF",
  },
};

export default function PhaseStrip({ phases, tasks }) {
  const phaseStats = useMemo(() => {
    return phases.map((p) => {
      const phaseTasks = tasks.filter((t) => t.phaseId === p.id);
      const total = phaseTasks.length;
      const progress =
        total === 0
          ? 0
          : Math.round(
              phaseTasks.reduce((s, t) => s + (t.progress ?? 0), 0) / total,
            );
      return { phase: p, total, progress };
    });
  }, [phases, tasks]);

  return (
    <div data-testid="phase-strip" className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {phaseStats.map(({ phase, total, progress }, idx) => {
        const meta = STATUS_PILL[phase.status];
        const color = phaseColorMap[phase.color];
        const isActive = phase.status === "in_progress";

        return (
          <div
            key={phase.id}
            data-testid={`phase-card-${phase.id}`}
            className={cn(
              "card-soft relative overflow-hidden rounded-[24px] p-5",
              isActive && "shadow-[0_24px_48px_-28px_rgba(124,58,237,0.25)]",
            )}
          >
            <div
              className="absolute left-0 top-0 h-1 w-full"
              style={{ background: color.solid }}
              aria-hidden
            />

            <div className="flex items-start justify-between gap-3">
              <div>
                <div
                  className="text-[10px] font-bold uppercase tracking-[0.18em]"
                  style={{ color: color.solid }}
                >
                  Phase {idx + 1} · {phase.name}
                </div>
                <div className="mt-1.5 text-[13px] font-bold text-zinc-900">
                  {phase.weeks}
                </div>
                <div className="mt-0.5 text-[12px] text-zinc-500">
                  {phase.tagline}
                </div>
              </div>
              <span
                data-testid={`phase-status-${phase.id}`}
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]",
                  meta.chip,
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 rounded-full",
                    isActive && "idea-dot-pulse",
                  )}
                  style={{ backgroundColor: meta.dot }}
                />
                {meta.label}
              </span>
            </div>

            <div className="mt-4 flex items-center justify-between text-[11px] text-zinc-500">
              <span>{total} tasks</span>
              <span className="font-semibold text-zinc-700">{progress}%</span>
            </div>

            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${progress}%`,
                  background: isActive
                    ? "linear-gradient(90deg, #0a0a0a 0%, #6D28D9 55%, #22D3EE 100%)"
                    : phase.status === "done"
                      ? "#10B981"
                      : "#D4D4D8",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
